/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import type { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary, ThemeProvider, ToastProvider } from '@vaas/react-ui-kit';
import { useTranslation } from 'react-i18next';

import { dismissBootSplash } from '@/components/loading/bootSplash';
import { queryClient } from '@/lib/queryClient';
import { THEME_CACHE_KEY } from '@/lib/themeCache';
import { AuthProvider } from '@/auth/AuthProvider';
import './toasts.css';

/** How long a toast stays before it auto-dismisses. */
const TOAST_MS = 5000;

/** Composes all app-wide providers (error boundary, auth, server-state cache, toasts). */
export function AppProviders({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <ErrorBoundary
      variant="screen"
      /*
       * The exception message used to be printed on the crash screen. It is behind the details
       * disclosure now, and only in dev: "Cannot read properties of undefined" tells a technician
       * nothing, and the sentence it displaced tells them what to do.
       */
      showDetails={import.meta.env.DEV}
      labels={{
        title: t('error.title'),
        description: t('error.description'),
        details: t('error.details'),
        retry: t('error.reload'),
      }}
      onError={(error, info) => {
        console.error('[ErrorBoundary]', error, info.componentStack);
        // A crash during boot would otherwise leave the splash covering this message.
        dismissBootSplash();
      }}
      /*
       * A render crash leaves the component tree unmountable, so resetting state in place would
       * usually just crash again; a reload is the honest recovery for an app-level boundary.
       */
      onReset={() => window.location.reload()}
    >
      {/*
       * The theme provider owns what is painted: the `data-theme` attribute, the `color-scheme`,
       * the OS-preference listener, and the local key the splash script replays before first paint.
       * The user-settings API stays the source of truth across devices - `useSyncUserSettings`
       * pushes the saved preference in here on load, and `useThemeToggle` pushes changes back out.
       *
       * `system` is the default rather than light, so that a first visit on a dark-mode machine
       * matches the splash instead of flashing light at it. The splash falls through to the OS when
       * it finds no stored preference, and now so does the app.
       */}
      <ThemeProvider storageKey={THEME_CACHE_KEY} defaultTheme="system">
        <QueryClientProvider client={queryClient}>
          {/*
           * Outside AuthProvider so a toast raised while signing in still has somewhere to land -
           * `queryClient`'s error handler fires for boot requests, before auth has settled.
           * Bottom-centre (the kit default, GMDS placement); toasts.css lifts the stack clear of
           * the feedback FAB and of a sticky bottom bar such as the Home disclaimer.
           */}
          <ToastProvider duration={TOAST_MS} labels={{ dismiss: t('toast.dismiss') }}>
            <AuthProvider>{children}</AuthProvider>
          </ToastProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
